import type {
  Schedule,
  ScheduleResponse,
  ScheduleImageResponse,
  DraftScheduleResponse,
  Period,
  PeriodResponse,
  LunchWave,
  LunchWaveResponse,
  DayType,
  ScheduleVariant,
  DayOfWeek,
  ScheduleModification,
} from '@/types/api';

const BROKEN_DAY_TYPE_ID = 'broken-day-type-id';
const MISSING_MODIFICATION = 'MISSING_MODIFICATION';

// Parse a "YYYY-MM-DD" string as a local date (not UTC)
const parseDateStringLocal = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Date format utilities
const formatResponseToTime = (time: string): string => {
  // Backend sends time in format like "08:00:00" or "08:00"
  // We want "08:00"
  return time.substring(0, 5);
};

// Validation helpers
const isDayOfWeekValid = (value: string): value is DayOfWeek => {
  return ['M', 'T', 'W', 'TH', 'F'].includes(value);
};

const isModificationValid = (value: string | undefined): value is ScheduleModification => {
  if (!value) return false;
  return [
    'ED',
    'EXAM',
    'LS60',
    'LS90',
    'LS105',
    'LS120',
    'LS150',
    'LS180',
    'HR',
    'ADVISORY',
    'ASSEMBLY',
    'ALL-PERIODS-MEET',
    'ACTIVITY',
    'PEP',
    'OTHER',
  ].includes(value);
};

// Parse schedule code (name field) to extract day type and modification
const parseScheduleCode = (
  scheduleResponse: ScheduleResponse
): [DayType, string | undefined] => {
  const [dayTypeName, modification] = scheduleResponse.name.split('!');

  return [
    { id: scheduleResponse.dayTypeId || BROKEN_DAY_TYPE_ID, name: dayTypeName },
    modification,
  ];
};

// Convert period response to Period type
export const periodResponseToPeriod = (periodResponse: PeriodResponse): Period => {
  const basePeriod = {
    id: periodResponse.id || '',
    name: periodResponse.name,
    startTime: formatResponseToTime(periodResponse.startTime),
    endTime: formatResponseToTime(periodResponse.endTime),
  };

  if (periodResponse.dayType && periodResponse.periodType) {
    return {
      ...basePeriod,
      dayType: {
        id: periodResponse.dayType.id,
        name: periodResponse.dayType.name,
      },
      periodType: {
        id: periodResponse.periodType.id,
        name: periodResponse.periodType.name,
      },
    };
  }

  return basePeriod as Period;
};

// Convert lunch wave response to LunchWave type
export const lunchWaveResponseToLunchWave = (
  lunchWaveResponse: LunchWaveResponse
): LunchWave => {
  return {
    id: lunchWaveResponse.id || '',
    name: lunchWaveResponse.name,
    slot: {
      id: lunchWaveResponse.lunchSlot.id || '',
      slot: lunchWaveResponse.lunchSlot.slot,
    },
    startTime: formatResponseToTime(lunchWaveResponse.startTime),
    endTime: formatResponseToTime(lunchWaveResponse.endTime),
  };
};

// Determine schedule variant from response
export const getScheduleVariantProps = (
  scheduleResponse: ScheduleResponse
): ScheduleVariant => {
  // CORE: static && !special
  if (scheduleResponse.static && !scheduleResponse.special) {
    const [dayType, dayOfWeek] = parseScheduleCode(scheduleResponse);

    if (!dayOfWeek) {
      return {
        variant: 'core',
        dayType,
        order: scheduleResponse.order,
        isInLsc: !scheduleResponse.hidden,
        isInAcf: !!scheduleResponse.grid,
      };
    }

    return {
      variant: 'core',
      dayType,
      order: scheduleResponse.order,
      isInLsc: !scheduleResponse.hidden,
      isInAcf: !!scheduleResponse.grid,
      ...(isDayOfWeekValid(dayOfWeek)
        ? { dayOfWeek }
        : { brokenDayOfWeek: dayOfWeek }),
    };
  }
  // MODIFIED: !static && !special
  else if (!scheduleResponse.static && !scheduleResponse.special) {
    const [dayType, modification] = parseScheduleCode(scheduleResponse);

    return {
      variant: 'modified',
      dayType,
      isInLsc: !scheduleResponse.hidden,
      ...(isModificationValid(modification) && modification !== 'OTHER'
        ? { modification }
        : {
            modification: 'OTHER',
            otherModification: modification || MISSING_MODIFICATION,
          }),
    };
  }
  // SPECIAL: !static && special
  else if (!scheduleResponse.static && scheduleResponse.special) {
    // Backend stores special schedule codes as "#-MM-DD-YYYY"
    const dateStr = scheduleResponse.name.replace('#-', '');
    const [month, day, year] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    return {
      variant: 'special',
      date,
    };
  } else {
    throw new Error(
      `Invalid schedule variant combination for ${scheduleResponse.name}`
    );
  }
};

// Parse full schedule response to Schedule type
export const parseScheduleResponse = (
  scheduleResponse: ScheduleResponse,
  imagesResponse?: ScheduleImageResponse[],
  draftSchedulesResponse?: DraftScheduleResponse[]
): Schedule => {
  if (!scheduleResponse.id) {
    throw new Error(`No id for ${scheduleResponse.name}`);
  }

  if (!scheduleResponse.schoolId) {
    throw new Error(`No school id for ${scheduleResponse.name}`);
  }

  if (!scheduleResponse.displayName) {
    throw new Error(`No display name for ${scheduleResponse.name}`);
  }

  const targetDate = draftSchedulesResponse?.find(
    (ds) => ds.id === scheduleResponse.id
  )?.targetDate;

  const variantProps = getScheduleVariantProps(scheduleResponse);

  return {
    createdAt: new Date(scheduleResponse.createdAt + 'Z'),
    updatedAt: new Date(scheduleResponse.updatedAt + 'Z'),
    author: scheduleResponse.author?.name,

    ...variantProps,

    id: scheduleResponse.id,
    schoolId: scheduleResponse.schoolId,
    originalScheduleCode: scheduleResponse.name,
    displayName: scheduleResponse.displayName,
    periods: scheduleResponse.periods.map(periodResponseToPeriod),
    lunchWaves: scheduleResponse.lunchWaves.map(lunchWaveResponseToLunchWave),

    isDraft: !!scheduleResponse.draft,
    isUserDraft: !!scheduleResponse.userDraft,
    image: imagesResponse?.find((i) => i.scheduleId === scheduleResponse.id)
      ?.imageViews[0],
    targetDate: targetDate ? parseDateStringLocal(targetDate) : undefined,
  } as Schedule;
};

// Helper to check if a period is a regular period (has dayType and periodType)
export const isRegularPeriod = (period: Period): period is Period & {
  dayType: DayType;
  periodType: { id: string; name: string };
} => {
  return 'dayType' in period && 'periodType' in period;
};

// Helper to check if schedule is ACF schedule
export const isAcfSchedule = (schedule: Partial<Schedule>): boolean => {
  return schedule.variant === 'core' && 'isInAcf' in schedule && schedule.isInAcf === true;
};

// Generate schedule code from schedule object (like old dashboard)
export const getScheduleCode = (schedule: Schedule): string => {
  if (schedule.variant === 'core') {
    return `${schedule.dayType.name}${schedule.dayOfWeek ? `!${schedule.dayOfWeek}` : ''}`;
  } else if (schedule.variant === 'modified') {
    const modification =
      schedule.modification === 'OTHER' && schedule.otherModification
        ? schedule.otherModification
        : schedule.modification;
    return `${schedule.dayType.name}!${modification}`;
  } else {
    const year = schedule.date.getFullYear();
    const month = String(schedule.date.getMonth() + 1).padStart(2, '0');
    const day = String(schedule.date.getDate()).padStart(2, '0');
    return `#-${month}-${day}-${year}`;
  }
};

